import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
// Utility to decode JWT without external libraries
const decodeJWT = (token) => {
  try {
    const payload = token.split(".")[1];
    // Convert from base64url to base64
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error("Failed to decode JWT", e);
    return null;
  }
};

const SESSION_INVALID_CODES = new Set(["SESSION_EXPIRED", "INVALID_TOKEN"]);
// Access permanently revoked server-side (company/environment soft-deleted).
// Refreshing the token cannot recover these — sign the user out immediately.
const ACCESS_REVOKED_CODES = new Set(["COMPANY_DELETED", "BU_DELETED"]);

// The session-expired modal must only ever surface on authenticated areas of
// the app (the tenant portal and the super-admin panel). On public pages
// (landing, login, signup, module/marketing pages, candidate evaluation, …)
// it should never appear.
const isPortalRoute = (pathname) => /^\/(portal|admin)(\/|$)/.test(pathname || "");

const getAuthErrorCode = (error) => {
  const data = error?.response?.data;
  const code = data?.detail || data?.message || data?.code;
  return typeof code === "string" ? code : null;
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // Retrieve stored tokens from localStorage on initial load
  const [authData, setAuthData] = useState(() => {
    const access_token = localStorage.getItem("access_token");
    const refresh_token = localStorage.getItem("refresh_token");
    const expires_at = localStorage.getItem("expires_at"); // timestamp (ms)
    if (access_token && refresh_token && expires_at) {
      // Re-attach default header for axios
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      const user = decodeJWT(access_token);
      return {
        access_token,
        refresh_token,
        expires_at: Number(expires_at),
        user
      };
    }
    return null;
  });

  // Keep a reference so we can cancel the scheduled refresh when logging out
  const refreshTimeoutRef = useRef(null);

  // Stable refs for functions used inside the axios interceptor.
  // This avoids re-registering interceptors on every render, which would
  // reset the in-flight request queue and cause race conditions / deadlocks.
  const refreshTokenRef = useRef(null);
  const logoutRef = useRef(null);

  const [isSessionExpiredModalOpen, setIsSessionExpiredModalOpen] = useState(false);
  const [sessionExpiredCode, setSessionExpiredCode] = useState(null);
  const sessionExpiredHandledRef = useRef(false);

  // --- Idle timeout (4h) ---
  const IDLE_TIMEOUT_MS = 4 * 60 * 60 * 1000; // 4 hours
  const IDLE_CHECK_INTERVAL_MS = 60 * 1000; // check every 60s
  const lastActivityRef = useRef(Date.now());
  const idleCheckIntervalRef = useRef(null);
  const navigate = useNavigate();

  const openSessionExpiredModal = useCallback((code) => {
    const hasRefreshToken = Boolean(localStorage.getItem("refresh_token"));
    if (!hasRefreshToken || sessionExpiredHandledRef.current) return;
    // Outside the portal/admin areas (i.e. on public pages) a dead session is
    // not actionable for the visitor — silently drop the stale tokens instead
    // of interrupting the public experience with a "Session expired" modal.
    const pathname = typeof window !== "undefined" ? window.location.pathname : "";
    if (!isPortalRoute(pathname)) {
      sessionExpiredHandledRef.current = true;
      logoutRef.current?.();
      return;
    }
    setIsSessionExpiredModalOpen((prev) => (prev ? prev : true));
    setSessionExpiredCode((prev) => (prev ?? code ?? null));
  }, []);

  const closeSessionExpiredModal = useCallback(() => {
    setIsSessionExpiredModalOpen(false);
    setSessionExpiredCode(null);
    sessionExpiredHandledRef.current = true;
  }, []);

  const clearRefreshTimeout = () => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  };

  const scheduleRefresh = useCallback(
    (expiresInSeconds) => {
      clearRefreshTimeout();
      // Refresh 1 minute (60s) before expiry, but never schedule a negative timeout
      const timeoutMs = Math.max((expiresInSeconds - 60) * 1000, 0);
      refreshTimeoutRef.current = setTimeout(() => {
        // Use ref to always call the latest refreshToken (with current authData)
        refreshTokenRef.current?.();
      }, timeoutMs);
    },
    []
    );
  

  
  const storeTokens = useCallback(
    ({ access_token, refresh_token, expires_in }) => {
      const expires_at = Date.now() + expires_in * 1000;
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      localStorage.setItem("expires_at", String(expires_at));

      // Attach token to axios headers for subsequent requests
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;

      const user = decodeJWT(access_token);
      setAuthData({ access_token, refresh_token, expires_at, user });
      sessionExpiredHandledRef.current = false;
      setIsSessionExpiredModalOpen(false);
      setSessionExpiredCode(null);
      scheduleRefresh(expires_in);
    },
    [scheduleRefresh]
  );

  const signup = async (payload) => {
    const { company_name, email, first_name, last_name, password } = payload;
    const url = `${process.env.REACT_APP_BASE_API_URL}/auth/signup`;
    const response = await axios.post(url, {
      company_name,
      email,
      first_name,
      last_name,
      password
    });
    // Backend now returns only a message (no tokens)
    return response.data; //  { message: "Account created. A verification email has been sent." }
  };

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BASE_API_URL}/auth/users/me`);
      const profile = res.data;
      setAuthData((prev) => (prev ? { ...prev, user: profile } : prev));
      return profile;
    } catch (err) {
      console.error("Failed to fetch user profile", err);
      return null;
    }
  }, []);

  const login = async (email, password) => {
    const url = `${process.env.REACT_APP_BASE_API_URL}/auth/login`;
    const response = await axios.post(url, {
      email,
      password
    });
    storeTokens(response.data);
    return fetchProfile();
  };

  const resendVerification = async (email) => {
    const url = `${process.env.REACT_APP_BASE_API_URL}/auth/resend-verification`;
    const response = await axios.post(url, { email });
    return response.data; // { message: "Verification email has been resent." }
  };

  const forgotPassword = async (email) => {
    const url = `${process.env.REACT_APP_BASE_API_URL}/auth/forgot-password`;
    const response = await axios.post(url, { email });
    return response.data; // { message: "If the account exists, an email has been sent." }
  };

  const refreshToken = async () => {
    if (!authData?.refresh_token) return false;
    try {
      const url = `${process.env.REACT_APP_BASE_API_URL}/auth/refresh`;
      const response = await axios.post(url, {
        refresh_token: authData.refresh_token
      });
      storeTokens(response.data);
      await fetchProfile();
      return true;
    } catch (error) {
      const errorCode = getAuthErrorCode(error);
      if (error?.response?.status === 401 && SESSION_INVALID_CODES.has(errorCode)) {
        openSessionExpiredModal(errorCode);
        return false;
      }
      console.error("Failed to refresh token", error);
      logout();
      return false;
    }
  };

  const logout = () => {
    clearRefreshTimeout();
    if (idleCheckIntervalRef.current) {
      clearInterval(idleCheckIntervalRef.current);
      idleCheckIntervalRef.current = null;
    }
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("expires_at");
    delete axios.defaults.headers.common["Authorization"];
    setAuthData(null);
  };

  // Keep refs in sync so the interceptor always calls the latest version
  refreshTokenRef.current = refreshToken;
  logoutRef.current = logout;

  // Set up global axios interceptors for attaching tokens and handling 401s.
  // Registered once (empty deps) — uses refs to call latest refreshToken/logout
  // so the queue (isRefreshing + failedQueue) is never torn down mid-flight.
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use((config) => {
      const token = localStorage.getItem("access_token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // Communicate user's preferred language to the backend.
      // Backend can use this to localize responses (incl. in-app notifications).
      try {
        const lang = localStorage.getItem("portalLanguage") || "en";
        config.headers["Accept-Language"] = lang;
      } catch {
        config.headers["Accept-Language"] = "en";
      }
      return config;
    });

    let isRefreshing = false;
    let failedQueue = [];

    const processQueue = (error, token = null) => {
      failedQueue.forEach((prom) => {
        if (error) {
          prom.reject(error);
        } else {
          prom.resolve(token);
        }
      });
      failedQueue = [];
    };

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const errorCode = getAuthErrorCode(error);
        const isRefreshRequest =
          originalRequest?.url?.includes("/auth/refresh") ?? false;
        // The company/environment was soft-deleted: access is permanently
        // revoked, so log the user out rather than attempting a token refresh.
        if (error?.response?.status === 403 && ACCESS_REVOKED_CODES.has(errorCode)) {
          logoutRef.current?.();
          return Promise.reject(error);
        }
        // Only treat SESSION_EXPIRED/INVALID_TOKEN as "session dead" without trying
        // refresh when the failing request was the refresh call itself. Otherwise
        // the backend may use these codes for expired *access* tokens; we should
        // try refresh first and only show the modal if refresh fails.
        // If the refresh request itself gets a 401, never queue/retry it —
        // that would deadlock (refresh waiting for itself to complete).
        if (error?.response?.status === 401 && isRefreshRequest) {
          if (SESSION_INVALID_CODES.has(errorCode)) {
            processQueue(error, null);
            openSessionExpiredModal(errorCode);
          }
          return Promise.reject(error);
        }
        if (
          error.response?.status === 401 &&
          localStorage.getItem("refresh_token") &&
          !originalRequest._retry
        ) {
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return axios(originalRequest);
              })
              .catch((err) => Promise.reject(err));
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            const refreshed = await refreshTokenRef.current?.();
            const newToken = localStorage.getItem("access_token");
            if (!refreshed || !newToken) {
              processQueue(error, null);
              return Promise.reject(error);
            }
            axios.defaults.headers.common.Authorization = `Bearer ${newToken}`;
            processQueue(null, newToken);
            return axios(originalRequest);
          } catch (err) {
            processQueue(err, null);
            logoutRef.current?.();
            return Promise.reject(err);
          } finally {
            isRefreshing = false;
          }
        }

        // Safety net: if a retried request (_retry = true) still gets 401 with
        // SESSION_EXPIRED / INVALID_TOKEN, the refresh "succeeded" at the
        // transport level but the session is dead. Show the modal.
        if (
          error.response?.status === 401 &&
          originalRequest._retry &&
          SESSION_INVALID_CODES.has(errorCode)
        ) {
          openSessionExpiredModal(errorCode);
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When authData changes (e.g., rehydrated tokens), schedule refresh accordingly
  useEffect(() => {
    if (authData) {
      const expiresIn = (authData.expires_at - Date.now()) / 1000;
      if (expiresIn > 0) {
        scheduleRefresh(expiresIn);
      } else {
        refreshToken();
      }
    } else {
      clearRefreshTimeout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData]);

  // Ensure profile (including role) is loaded after hydration/login
  useEffect(() => {
    if (authData && (!authData.user || !authData.user.role)) {
      fetchProfile();
    }
  }, [authData, fetchProfile]);

  // --- Idle timeout: track user activity and auto-logout after 4h of inactivity ---
  useEffect(() => {
    if (!authData) {
      // Not logged in — clear any idle timers
      if (idleCheckIntervalRef.current) {
        clearInterval(idleCheckIntervalRef.current);
        idleCheckIntervalRef.current = null;
      }
      return;
    }

    const resetActivity = () => {
      lastActivityRef.current = Date.now();
    };

    const activityEvents = ["mousemove", "keydown", "click", "scroll"];
    activityEvents.forEach((evt) => window.addEventListener(evt, resetActivity, { passive: true }));

    idleCheckIntervalRef.current = setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current;
      if (idleMs >= IDLE_TIMEOUT_MS) {
        logoutRef.current?.();
        navigate("/login");
      }
    }, IDLE_CHECK_INTERVAL_MS);

    return () => {
      activityEvents.forEach((evt) => window.removeEventListener(evt, resetActivity));
      if (idleCheckIntervalRef.current) {
        clearInterval(idleCheckIntervalRef.current);
        idleCheckIntervalRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData]);

  const value = {
    authData,
    isAuthenticated: Boolean(authData),
    user: authData?.user,
    signup,
    login,
    fetchProfile,
    logout,
    resendVerification,
    forgotPassword,
    isSessionExpiredModalOpen,
    sessionExpiredCode,
    closeSessionExpiredModal
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext); 
